import { useState, useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import Async from 'react-select/async';
import { toast } from 'react-toastify';

import Modal from '../Modal';
import { RadioList } from '../Filters/components/RadioList';
import { API } from '../../API';
import './index.css';

const STEP_SELECT_WORKSPACE = 'select_workspace';
const STEP_GROUP_STRATEGY = 'group_strategy';
const STEP_CONFIRM_PRIVATE_SHARE = 'confirm_private_share';

export default function CopyNarrativeToWorkspacePopup({ isVisible, onClose, narrativeId }) {
  const { t } = useTranslation();
  const [workspace, setWorkspace] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [workspaceSelect, setWorkspaceSelect] = useState(false);
  const [step, setStep] = useState(STEP_SELECT_WORKSPACE);
  const [unavailableGroups, setUnavailableGroups] = useState([]);
  const [groupStrategy, setGroupStrategy] = useState('copy');

  const privateGroups = unavailableGroups.filter((group) => group.visibility === 'PRIVATE');

  const copyMessage = () => {
    toast.info(t('This process may take a few minutes. Please wait before checking the changes.'));
  }

  useEffect(() => {
    if (isVisible) {
        API.fetch('GET', `/API/v1/workspaces?size=100`).then((data) => {
            setWorkspaces(data.objects);
        });
    }
  }, [isVisible]);

  const getWorkspaces = (inputValue, callback) => {
    return API.fetch('GET', `/API/v1/workspaces?q=${inputValue}`).then((data) => {
      callback(
        data.objects
          .sort((a, b) => (a.name > b.name ? 1 : -1))
          .map((workspace) => {
            return { value: workspace.id, label: workspace.name };
          }),
      );
    });
  }

  let workspacesPromiseTimeout;
  const promiseOptions = function (inputValue) {
    if (workspacesPromiseTimeout !== null) {
      clearTimeout(workspacesPromiseTimeout);
    }

    if (! inputValue) {
        return [];
    }

    return new Promise((resolve) => {
      workspacesPromiseTimeout = setTimeout(() => {
        getWorkspaces(inputValue, resolve);
      }, 1000);
    });
  }

  useEffect(() => {
    const container = document.querySelector('#copy-narrative-modal-container');
    if (!container) return;
    if (workspaceSelect) {
      const content = document.querySelector('.copy-narrative-modal__menu');
      if (content && container.offsetHeight < content.offsetHeight) {
        container.style.height = container.offsetHeight + content.offsetHeight + 'px';
      }
    } else {
      container.style.height = '';
    }
  }, [workspaceSelect]);

  const requestControllerRef = useRef(null);

  const handleClose = () => {
    requestControllerRef.current?.abort();
    setIsLoading(false);
    setStep(STEP_SELECT_WORKSPACE);
    setUnavailableGroups([]);
    setGroupStrategy('copy');
    onClose();
  };

  const handleCopyNarrative = (strategy, confirmPrivateShare) => {
    setIsLoading(true);
    const controller = new AbortController();
    requestControllerRef.current = controller;
    const params = new URLSearchParams();
    if (strategy) {
      params.set('group_strategy', strategy);
    }
    if (confirmPrivateShare) {
      params.set('confirm_private_share', 'true');
    }
    const query = params.toString();
    const url = `/API/v1/admin/narratives/${narrativeId}/workspaces/${workspace.value}${query ? `?${query}` : ''}`;

    API.fetch('POST', url, null, {}, controller.signal).then(() => {
      if (controller.signal.aborted) {
        return;
      }
      setIsLoading(false);
      copyMessage();
      handleClose();
    }).catch((error) => {
      if (controller.signal.aborted) {
        return;
      }
      setIsLoading(false);
      const detail = error?.cause?.detail;
      if (detail?.code === 'case_source_groups_unavailable') {
        setUnavailableGroups(detail.groups || []);
        setGroupStrategy('copy');
        setStep(STEP_GROUP_STRATEGY);
        return;
      }
      toast.error(t('An error occurred while copying the narrative'));
    }
    );
  };

  const handleStrategyContinue = () => {
    if (groupStrategy === 'share' && privateGroups.length > 0) {
      setStep(STEP_CONFIRM_PRIVATE_SHARE);
      return;
    }
    handleCopyNarrative(groupStrategy, false);
  };

  const selectWorkspaceContent = (
    <div id='copy-narrative-modal-container' className='copy-narrative-wrapper'>
        <div>
            {t('Are you sure you want to copy these publications from a narrative?')}
        </div>
        <div className='copy-narrative-hint'>
            {t('Note: only existing data is copied, not the running processes. Topic modeling and stories still being processed in this narrative will not appear in the copy. It is better to wait until they complete before copying.')}
        </div>
        <Async
          cacheOptions
          defaultOptions={workspaces.map(i => ({value: i.id, label: i.name}))}
          onChange={(value) => setWorkspace(value)}
          classNamePrefix="copy-narrative-modal"
          value={workspace}
          loadOptions={promiseOptions}
          placeholder={t('Please enter at least 1 symbol to search')}
          noOptionsMessage={() => t('Please enter at least 1 symbol')}
          loadingMessage={() => t('Loading...')}
          onMenuOpen={() => setWorkspaceSelect(true)}
          onMenuClose={() => setWorkspaceSelect(false)}
          menuIsOpen={workspaceSelect}
        />
      </div>
  );

  const groupStrategyContent = (
    <div className='copy-narrative-wrapper'>
      <div className='copy-narrative-hint'>
        {t('The following actor groups are not available in the target workspace:')}
        <ul className='copy-narrative-groups'>
          {unavailableGroups.map((group) => (
            <li key={group.id}>
              {group.name}
              <span className='copy-narrative-group-visibility'>{t(group.visibility?.toLowerCase())}</span>
            </li>
          ))}
        </ul>
      </div>
      <RadioList
        name='group-strategy'
        selectedValue={groupStrategy}
        onChange={(value) => setGroupStrategy(value)}
        items={[
          {
            name: t('Copy groups to the target workspace'),
            value: 'copy',
            description: t('Independent copies of the groups will be created; further changes to the original groups will not affect them.'),
          },
          {
            name: t('Share groups with the target workspace'),
            value: 'share',
            description: t('The case will use the original groups; further changes to them will affect the copied case.'),
          },
        ]}
      />
    </div>
  );

  const confirmPrivateShareContent = (
    <div className='copy-narrative-wrapper'>
      <div className='copy-narrative-hint'>
        {t('Groups {{groups}} are currently private. Sharing will make them sharable and reveal their name, description and actor list to workspace {{workspace}}. Continue?', {
          groups: privateGroups.map((group) => group.name).join(', '),
          workspace: workspace?.label,
        })}
      </div>
    </div>
  );

  const stepContent = {
    [STEP_SELECT_WORKSPACE]: selectWorkspaceContent,
    [STEP_GROUP_STRATEGY]: groupStrategyContent,
    [STEP_CONFIRM_PRIVATE_SHARE]: confirmPrivateShareContent,
  };

  const stepFooter = {
    [STEP_SELECT_WORKSPACE]: (
      <>
        <button
          className="new-button"
          disabled={isLoading || !workspace}
          onClick={() => handleCopyNarrative(null, false)}
        >
          {t('Copy')}
        </button>
        <button
          className="btn-reset"
          onClick={handleClose}
        >
          {t('Cancel')}
        </button>
      </>
    ),
    [STEP_GROUP_STRATEGY]: (
      <>
        <button
          className="new-button"
          disabled={isLoading}
          onClick={handleStrategyContinue}
        >
          {t('Continue')}
        </button>
        <button
          className="btn-reset"
          onClick={handleClose}
        >
          {t('Cancel')}
        </button>
      </>
    ),
    [STEP_CONFIRM_PRIVATE_SHARE]: (
      <>
        <button
          className="new-button"
          disabled={isLoading}
          onClick={() => handleCopyNarrative('share', true)}
        >
          {t('Yes, share')}
        </button>
        <button
          className="btn-reset"
          onClick={() => setStep(STEP_GROUP_STRATEGY)}
        >
          {t('Back')}
        </button>
      </>
    ),
  };

  return (
    <Modal
        isVisible={isVisible}
        doNotCloseOnOutsideClick={true}
        title={t('Copy a narrative to anoter workspace')}
        content={stepContent[step]}
        footer={stepFooter[step]}
        onClose={handleClose}
      />
  );
}
